import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { AcademicianStartupButton } from "@/components/academician/academicianstartup-button";
import { PlusIcon, PencilIcon, TrashIcon } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export type PainPointStatus =
  | "hypothesis"
  | "testing"
  | "validated"
  | "invalidated";

export interface PainPoint {
  id: string;
  painPoint: string;
  segment: string;
  status: PainPointStatus;
}

export interface SegmentOption {
  id: string;
  name: string;
}

export interface PainPointsTabProps {
  painPoints?: PainPoint[];
  segments?: SegmentOption[];
  onAdd?: (painPoint: Omit<PainPoint, "id">) => void;
  onEdit?: (id: string, painPoint: Omit<PainPoint, "id">) => void;
  onDelete?: (id: string) => void;
  className?: string;
}

const statusConfig: Record<
  PainPointStatus,
  {
    label: string;
    variant: "default" | "secondary" | "destructive" | "outline";
  }
> = {
  hypothesis: { label: "Hypothesis", variant: "secondary" },
  testing: { label: "Testing", variant: "outline" },
  validated: { label: "Validated", variant: "default" },
  invalidated: { label: "Invalidated", variant: "destructive" },
};

export function PainPointsTab({
  painPoints = [],
  segments = [],
  onAdd,
  onEdit,
  onDelete,
  className,
}: PainPointsTabProps) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    painPoint: "",
    segment: "",
    status: "hypothesis" as PainPointStatus,
  });

  const handleOpenDialog = (painPoint?: PainPoint) => {
    if (painPoint) {
      setEditingId(painPoint.id);
      setFormData({
        painPoint: painPoint.painPoint,
        segment: painPoint.segment,
        status: painPoint.status,
      });
    } else {
      setEditingId(null);
      setFormData({
        painPoint: "",
        segment: "",
        status: "hypothesis",
      });
    }
    setIsDialogOpen(true);
  };

  const handleSave = () => {
    if (editingId) {
      onEdit?.(editingId, formData);
    } else {
      onAdd?.(formData);
    }
    setIsDialogOpen(false);
  };

  return (
    <div className={className}>
      {/* Add Button */}
      <div className="flex justify-end mb-4">
        <AcademicianStartupButton
          variant="primary"
          leftIcon={<PlusIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Pain Point
        </AcademicianStartupButton>
      </div>

      {/* Table */}
      <p className="text-sm font-semibold text-primary italic mb-2">For Customer Discovery and Validation Purposes</p>
      <div className="border border-border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[40%]">Pain Point</TableHead>
              <TableHead className="w-[25%]">Customer</TableHead>
              <TableHead className="w-[20%]">Status</TableHead>
              <TableHead className="w-[15%] text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {painPoints.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={4}
                  className="text-center py-8 text-muted-foreground"
                >
                  No pain points yet. Click "Add Pain Point" to get started.
                </TableCell>
              </TableRow>
            ) : (
              painPoints.map((painPoint) => (
                <TableRow key={painPoint.id}>
                  <TableCell className="font-medium">
                    {painPoint.painPoint}
                  </TableCell>
                  <TableCell>{painPoint.segment}</TableCell>
                  <TableCell>
                    <Badge
                      variant={statusConfig[painPoint.status].variant}
                      className={
                        painPoint.status === "testing"
                          ? "bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 border-amber-300 dark:border-amber-700"
                          : painPoint.status === "validated"
                            ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 border-green-300 dark:border-green-700"
                            : ""
                      }
                    >
                      {statusConfig[painPoint.status].label}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <AcademicianStartupButton
                        variant="ghost"
                        size="sm"
                        leftIcon={<PencilIcon />}
                        onClick={() => handleOpenDialog(painPoint)}
                        className="px-2"
                      />

                      <AcademicianStartupButton
                        variant="ghost"
                        size="sm"
                        leftIcon={<TrashIcon />}
                        onClick={() => onDelete?.(painPoint.id)}
                        className="px-2 text-destructive hover:text-destructive"
                      />
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingId ? "Edit Pain Point" : "Add Pain Point"}
            </DialogTitle>
            <DialogDescription>
              {editingId
                ? "Update the pain point details below."
                : "Add a new pain point to track and validate."}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="painPoint">Pain Point</Label>
              <Input
                id="painPoint"
                value={formData.painPoint}
                onChange={(e) =>
                  setFormData({ ...formData, painPoint: e.target.value })
                }
                placeholder="Describe the pain point"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="segment">Customer</Label>
              {segments.length > 0 ? (
                <Select
                  value={formData.segment}
                  onValueChange={(value) =>
                    setFormData({ ...formData, segment: value })
                  }
                >
                  <SelectTrigger id="segment">
                    <SelectValue placeholder="Select a customer" />
                  </SelectTrigger>
                  <SelectContent>
                    {segments.map((seg) => (
                      <SelectItem key={seg.id} value={seg.name}>
                        {seg.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  id="segment"
                  value={formData.segment}
                  onChange={(e) =>
                    setFormData({ ...formData, segment: e.target.value })
                  }
                  placeholder="Add customers in the Customers tab first"
                />
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select
                value={formData.status}
                onValueChange={(value) =>
                  setFormData({ ...formData, status: value as PainPointStatus })
                }
              >
                <SelectTrigger id="status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="hypothesis">Hypothesis</SelectItem>
                  <SelectItem value="testing">Testing</SelectItem>
                  <SelectItem value="validated">Validated</SelectItem>
                  <SelectItem value="invalidated">Invalidated</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <AcademicianStartupButton
              variant="secondary"
              onClick={() => setIsDialogOpen(false)}
            >
              Cancel
            </AcademicianStartupButton>
            <AcademicianStartupButton
              variant="primary"
              onClick={handleSave}
              disabled={!formData.painPoint || !formData.segment}
            >
              {editingId ? "Save Changes" : "Add Pain Point"}
            </AcademicianStartupButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
